import React, { useMemo, useState } from "react";
import type { TaktBoardResponse } from "../../../models";
import {
  activeFilterKeys,
  facetCounts,
  type ColorByMode,
  type CompareOp,
  type FilterState,
  type MatchMode,
  type TaktAttribute,
} from "../filtering/attributes";
import FacetSection from "./FacetSection/FacetSection";
import "./FilterRail.css";

const COLOR_OPTIONS: { id: ColorByMode; label: string }[] = [
  { id: "none", label: "No color" },
  { id: "workCode", label: "Work code" },
  { id: "trade", label: "Trade" },
  { id: "critical", label: "Critical path" },
];

interface Props {
  data: TaktBoardResponse;
  attributes: TaktAttribute[];
  filters: FilterState;
  matchMode: MatchMode;
  shownActivities: number;
  totalActivities: number;
  shownSections: number;
  /** Color dimension (the View rail owns coloring now, not a separate menu). */
  colorBy: ColorByMode;
  onSetColorBy: (m: ColorByMode) => void;
  onOpenManageTrades: () => void;
  onToggleValue: (attrKey: string, valueId: string) => void;
  onSetCompare: (attrKey: string, op: CompareOp, value: number | null) => void;
  onClearAttr: (attrKey: string) => void;
  onClearAll: () => void;
  onSetMatchMode: (m: MatchMode) => void;
  /** Navigate to a value of a navigable attribute (e.g. scroll to a section). */
  onNavigateValue: (attrKey: string, valueId: string) => void;
  onClose: () => void;
}

/**
 * The docked Filters panel — a dedicated area beside the board (not a floating
 * dropdown). Each dimension is a collapsible section; the panel scrolls
 * internally so it scales to many dimensions and filter types (sets + ranges).
 */
const FilterRail: React.FC<Props> = ({
  data,
  attributes,
  filters,
  matchMode,
  shownActivities,
  totalActivities,
  shownSections,
  colorBy,
  onSetColorBy,
  onOpenManageTrades,
  onToggleValue,
  onSetCompare,
  onClearAttr,
  onClearAll,
  onSetMatchMode,
  onNavigateValue,
  onClose,
}) => {
  // All facets start collapsed; the rail opens as a compact list you expand
  // into on demand.
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const activeKeys = activeFilterKeys(filters);
  const anyActive = activeKeys.length > 0;

  const countsByAttr = useMemo(() => {
    const m: Record<string, Record<string, number>> = {};
    for (const a of attributes) {
      m[a.key] =
        a.kind === "range" ? {} : facetCounts(data, attributes, filters, matchMode, a.key);
    }
    return m;
  }, [data, attributes, filters, matchMode]);

  const toggleExpand = (key: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  return (
    <div className="filter-rail">
      <div className="filter-rail__header">
        <div className="filter-rail__heading">
          <span className="filter-rail__title">View</span>
          <span className="filter-rail__summary">
            <strong>{shownActivities}</strong> of {totalActivities} activities ·{" "}
            {shownSections} {shownSections === 1 ? "section" : "sections"}
          </span>
        </div>
        <button
          type="button"
          className="filter-rail__close"
          onClick={onClose}
          title="Hide filters"
        >
          ×
        </button>
      </div>

      <div className="filter-rail__body">
        <div className="filter-rail__colorby">
          <span className="filter-rail__colorby-label">Color by</span>
          <select
            className="filter-rail__colorby-select"
            value={colorBy}
            onChange={(e) => onSetColorBy(e.target.value as ColorByMode)}
          >
            {COLOR_OPTIONS.map((o) => (
              <option key={o.id} value={o.id}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        {attributes.map((attr) => (
          <FacetSection
            key={attr.key}
            attr={attr}
            selected={filters[attr.key] || []}
            counts={countsByAttr[attr.key] || {}}
            expanded={expanded.has(attr.key)}
            onToggleExpand={() => toggleExpand(attr.key)}
            onToggleValue={(vid) => onToggleValue(attr.key, vid)}
            onSetCompare={(o, value) => onSetCompare(attr.key, o, value)}
            onClear={() => onClearAttr(attr.key)}
            onNavigate={
              attr.navigable ? (vid) => onNavigateValue(attr.key, vid) : undefined
            }
            headerLink={
              attr.key === "trade"
                ? { label: "Manage trades", onClick: onOpenManageTrades }
                : undefined
            }
          />
        ))}
      </div>

      <div className="filter-rail__footer">
        {activeKeys.length >= 2 && (
          <div className="filter-rail__match">
            <span className="filter-rail__match-label">Match</span>
            <div className="seg">
              <button
                className={`seg-btn${matchMode === "all" ? " seg-btn--active" : ""}`}
                onClick={() => onSetMatchMode("all")}
              >
                All
              </button>
              <button
                className={`seg-btn${matchMode === "any" ? " seg-btn--active" : ""}`}
                onClick={() => onSetMatchMode("any")}
              >
                Any
              </button>
            </div>
          </div>
        )}
        <button
          type="button"
          className="filter-rail__clear"
          onClick={onClearAll}
          disabled={!anyActive}
        >
          Clear all
        </button>
      </div>
    </div>
  );
};

export default FilterRail;
